import { API_BASE, get, post } from '../api';
import { iconCheck, iconCopy, iconKey, iconMessageSquare, iconPhone, iconShield, iconX } from '../icons';
import { showModal, closeModal, modalFrame, confirmModal } from './modal';
import { showToast } from './toast';

const MITCHELL_SERVER_ID = 'bdd20d6c10de657d-d160aae7-94f32953-7be68ad2c1084a24';

type GuestSlug = 'ma' | 'james';

interface NamedGuestDef {
  label: string;
  slug: GuestSlug;
}

interface NamedGuestLink {
  id: string;
  server_id: string;
  guest_label: string;
  guest_slug: string;
  status: string;
  text_channel_id: string | null;
  voice_channel_id: string | null;
  category_name: string;
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
  failed_attempts: number;
  locked_until: string | null;
  expires_at: string | null;
  revoked_at: string | null;
}

interface NamedGuestEnsureResp {
  id: string;
  token: string | null;
  status: string;
  text_channel_id: string;
  voice_channel_id: string;
}

interface GuestAccessRequest {
  id: string;
  guest_slug: string;
  message: string;
  status: 'pending' | 'approved' | 'denied';
  created_at: string;
}

const NAMED_GUESTS: NamedGuestDef[] = [
  { label: 'Ma', slug: 'ma' },
  { label: 'James', slug: 'james' },
];

let oneTimeUrls = new Map<GuestSlug, string>();

export function isNamedGuestCategory(category: string): boolean {
  const normalized = category.trim().toLowerCase();
  return normalized === 'ma' || normalized === 'james';
}

export function namedGuestDefForCategory(category: string): NamedGuestDef | null {
  const normalized = category.trim().toLowerCase();
  return NAMED_GUESTS.find(g => g.slug === normalized) ?? null;
}

export function openNamedGuestCategorySettings(category: string, onChanged?: () => void): void {
  const guest = namedGuestDefForCategory(category);
  if (!guest) return;
  showModal(modalFrame(`${guest.label} Settings`, `<div id="ngc-modal-root" class="ngc-wrap"><div class="ngc-empty">Loading...</div></div>`, ''), { wide: true });
  document.getElementById('modal-close')?.addEventListener('click', closeModal);
  const root = document.getElementById('ngc-modal-root');
  if (root) void paintGuestControls(root, [guest], onChanged);
}

export function mountNamedGuestDiagnostics(container: HTMLElement): void {
  const panel = container.querySelector<HTMLElement>('#guest-access-panel');
  if (!panel) return;
  const load = (): void => {
    void paintGuestControls(panel, NAMED_GUESTS);
  };
  container.querySelector('#guest-access-refresh')?.addEventListener('click', load);
  load();
}

async function paintGuestControls(root: HTMLElement, guests: NamedGuestDef[], onChanged?: () => void): Promise<void> {
  const [links, requests] = await Promise.all([
    get<NamedGuestLink[]>(`/api/named-guests/list?server_id=${encodeURIComponent(MITCHELL_SERVER_ID)}`).catch(() => [] as NamedGuestLink[]),
    get<GuestAccessRequest[]>(`/api/named-guests/access-requests?server_id=${encodeURIComponent(MITCHELL_SERVER_ID)}`).catch(() => [] as GuestAccessRequest[]),
  ]);
  root.innerHTML = `<div class="ngc-grid">${guests.map(guest => {
    const link = links.find(l => l.guest_slug === guest.slug) ?? null;
    const guestRequests = requests.filter(r => r.guest_slug === guest.slug);
    return renderGuestPanel(guest, link, guestRequests);
  }).join('')}</div>`;
  wireGuestControls(root, guests, onChanged);
}

function renderGuestPanel(guest: NamedGuestDef, row: NamedGuestLink | null, requests: GuestAccessRequest[]): string {
  const status = row?.status ?? 'not created';
  const oneTimeUrl = oneTimeUrls.get(guest.slug) ?? '';
  const pending = requests.filter(r => r.status === 'pending');
  return `
    <section class="ngc-card" data-ngc-slug="${escAttr(guest.slug)}" data-ngc-label="${escAttr(guest.label)}" data-ngc-link-id="${escAttr(row?.id ?? '')}">
      <header class="ngc-head">
        <div>
          <div class="ngc-title">${escHtml(guest.label)} guest access</div>
          <div class="ngc-sub">Access is controlled by Mitchell</div>
        </div>
        <span class="${escAttr(statusClass(status))}">${escHtml(statusLabel(status))}</span>
      </header>

      <div class="ngc-status-grid">
        ${statusItem(iconMessageSquare(), 'Text channel', row?.text_channel_id ? 'Ready' : 'Missing')}
        ${statusItem(iconPhone(), 'Call channel', row?.voice_channel_id ? 'Ready' : 'Missing')}
        ${statusItem(iconCheck(), 'Last active', row?.last_used_at ? fmtDate(row.last_used_at) : 'Never')}
        ${statusItem(iconShield(), 'Receipts', 'Read receipts enabled')}
      </div>

      ${oneTimeUrl ? `
        <div class="ngc-once">
          <label class="form-label" for="ngc-url-${escAttr(guest.slug)}">New link shown once</label>
          <div class="ngc-copy-row">
            <input id="ngc-url-${escAttr(guest.slug)}" class="form-input" value="${escAttr(oneTimeUrl)}" readonly>
            <button class="btn btn-secondary ngc-touch" type="button" data-ngc-action="copy-once">${iconCopy()}<span>Copy link</span></button>
          </div>
        </div>
      ` : `
        <div class="ngc-muted-note">Create or regenerate a link to reveal a copyable URL once.</div>
      `}

      <div class="ngc-actions">
        <button class="btn btn-secondary ngc-touch" type="button" data-ngc-action="ensure">Ensure ${escHtml(guest.label)} space</button>
        <button class="btn btn-primary ngc-touch" type="button" data-ngc-action="create-link">${iconKey()}<span>Generate/create link</span></button>
        <button class="btn btn-secondary ngc-touch" type="button" data-ngc-action="copy-once" ${oneTimeUrl ? '' : 'disabled'}>${iconCopy()}<span>Copy link</span></button>
        <button class="btn btn-secondary ngc-touch" type="button" data-ngc-action="${status === 'disabled' ? 'enable' : 'disable'}" ${row ? '' : 'disabled'}>${status === 'disabled' ? 'Re-enable access' : 'Disable access'}</button>
        <button class="btn btn-secondary ngc-touch" type="button" data-ngc-action="regenerate" ${row ? '' : 'disabled'}>Regenerate link</button>
        <button class="btn btn-secondary ngc-touch" type="button" data-ngc-action="reset-pin" ${row ? '' : 'disabled'}>Reset PIN</button>
      </div>

      <section class="ngc-requests">
        <div class="ngc-section-title">Pending access requests</div>
        ${pending.length === 0 ? '<div class="ngc-empty">No pending requests.</div>' : pending.map(renderRequest).join('')}
        ${requests.filter(r => r.status !== 'pending').slice(0, 3).map(renderRequest).join('')}
      </section>
    </section>
  `;
}

function renderRequest(req: GuestAccessRequest): string {
  const isPending = req.status === 'pending';
  return `
    <article class="ngc-request" data-ngc-request-id="${escAttr(req.id)}">
      <div class="ngc-request-top">
        <strong>${escHtml(labelForSlug(req.guest_slug))}</strong>
        <span class="${escAttr(requestStatusClass(req.status))}">${escHtml(req.status)}</span>
      </div>
      <div class="ngc-request-message">${escHtml(req.message)}</div>
      <time class="ngc-request-time">${escHtml(fmtDate(req.created_at))}</time>
      ${isPending ? `
        <div class="ngc-request-actions">
          <button class="btn btn-secondary ngc-touch" type="button" data-ngc-action="approve-request">Re-enable access</button>
          <button class="btn btn-secondary ngc-touch" type="button" data-ngc-action="deny-request">${iconX()}<span>Deny</span></button>
        </div>
      ` : ''}
    </article>
  `;
}

function statusItem(icon: string, label: string, value: string): string {
  return `<div class="ngc-status-item">${icon}<span>${escHtml(label)}</span><strong>${escHtml(value)}</strong></div>`;
}

function wireGuestControls(root: HTMLElement, guests: NamedGuestDef[], onChanged?: () => void): void {
  root.querySelectorAll<HTMLButtonElement>('[data-ngc-action]').forEach(btn => {
    btn.addEventListener('click', () => {
      void runAction(root, btn, guests, onChanged);
    });
  });
}

async function runAction(root: HTMLElement, btn: HTMLButtonElement, guests: NamedGuestDef[], onChanged?: () => void): Promise<void> {
  const card = btn.closest<HTMLElement>('[data-ngc-slug]');
  if (!card) return;
  const slug = card.dataset['ngcSlug'] as GuestSlug | undefined;
  const label = card.dataset['ngcLabel'] ?? '';
  const linkId = card.dataset['ngcLinkId'] ?? '';
  const action = btn.dataset['ngcAction'] ?? '';
  const requestId = btn.closest<HTMLElement>('[data-ngc-request-id]')?.dataset['ngcRequestId'] ?? '';
  if (!slug) return;

  if (action === 'disable') {
    confirmModal('Disable guest access', `Disable ${label} access?`, () => {
      void performAction(root, guests, slug, label, linkId, action, requestId, onChanged);
    }, { confirmLabel: 'Disable', variant: 'danger' });
    return;
  }
  if (action === 'regenerate') {
    confirmModal('Regenerate guest link', `Regenerate ${label} link? The old URL will stop working.`, () => {
      void performAction(root, guests, slug, label, linkId, action, requestId, onChanged);
    }, { confirmLabel: 'Regenerate', variant: 'danger' });
    return;
  }
  if (action === 'create-link' && linkId) {
    confirmModal('Regenerate guest link', `Create a new ${label} link? The old URL will stop working.`, () => {
      void performAction(root, guests, slug, label, linkId, action, requestId, onChanged);
    }, { confirmLabel: 'Create new link', variant: 'danger' });
    return;
  }
  await performAction(root, guests, slug, label, linkId, action, requestId, onChanged);
}

async function performAction(root: HTMLElement, guests: NamedGuestDef[], slug: GuestSlug, label: string, linkId: string, action: string, requestId: string, onChanged?: () => void): Promise<void> {
  try {
    if (action === 'copy-once') {
      const url = oneTimeUrls.get(slug) ?? '';
      if (!url) return;
      await copyText(url);
      showToast('Link copied', 'ok');
      return;
    }
    if (action === 'ensure') {
      await post<NamedGuestEnsureResp>('/api/named-guests/ensure', { server_id: MITCHELL_SERVER_ID, guest_label: label, guest_slug: slug });
      showToast(`${label} space ready`, 'ok');
      onChanged?.();
    } else if (action === 'create-link') {
      if (linkId) {
        const res = await post<NamedGuestEnsureResp>('/api/named-guests/regenerate', { link_id: linkId });
        rememberOneTimeUrl(slug, res.token);
      } else {
        const res = await post<NamedGuestEnsureResp>('/api/named-guests/ensure', { server_id: MITCHELL_SERVER_ID, guest_label: label, guest_slug: slug });
        rememberOneTimeUrl(slug, res.token);
      }
      showToast('New guest link ready', 'ok');
      onChanged?.();
    } else if (action === 'enable' || action === 'disable' || action === 'reset-pin' || action === 'regenerate') {
      if (!linkId) return;
      const endpoint = action === 'reset-pin' ? 'reset-pin' : action;
      const res = await post<NamedGuestEnsureResp | string>(`/api/named-guests/${endpoint}`, { link_id: linkId });
      if (action === 'regenerate' && isEnsureResp(res)) rememberOneTimeUrl(slug, res.token);
      showToast('Guest access updated', 'ok');
      onChanged?.();
    } else if (action === 'approve-request' || action === 'deny-request') {
      if (!requestId) return;
      if (action === 'approve-request' && linkId) {
        await post('/api/named-guests/enable', { link_id: linkId });
      }
      await post('/api/named-guests/access-requests/status', {
        request_id: requestId,
        status: action === 'approve-request' ? 'approved' : 'denied',
      });
      showToast(action === 'approve-request' ? 'Access re-enabled' : 'Request denied', 'ok');
    }
    await paintGuestControls(root, guests, onChanged);
  } catch (err) {
    showToast(err instanceof Error ? err.message : 'Guest access action failed', 'err');
  }
}

function rememberOneTimeUrl(slug: GuestSlug, token: string | null): void {
  if (!token) return;
  oneTimeUrls.set(slug, namedGuestUrl(slug, token));
}

function isEnsureResp(value: NamedGuestEnsureResp | string): value is NamedGuestEnsureResp {
  return typeof value === 'object' && value !== null && 'token' in value;
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const input = document.createElement('input');
  input.value = value;
  document.body.appendChild(input);
  input.select();
  document.execCommand('copy');
  input.remove();
}

function namedGuestUrl(slug: GuestSlug, token: string): string {
  const base = `${window.location.origin}${API_BASE}`;
  return `${base}/${encodeURIComponent(slug)}?token=${encodeURIComponent(token)}`;
}

function labelForSlug(slug: string): string {
  return slug === 'james' ? 'James' : 'Ma';
}

function statusLabel(status: string): string {
  if (status === 'pending_pin') return 'PIN setup pending';
  if (status === 'active') return 'Active';
  if (status === 'disabled') return 'Disabled';
  return status;
}

function statusClass(status: string): string {
  if (status === 'active') return 'badge badge-ok';
  if (status === 'disabled' || status === 'revoked') return 'badge badge-err';
  if (status === 'pending_pin') return 'badge badge-warn';
  return 'badge badge-muted';
}

function requestStatusClass(status: GuestAccessRequest['status']): string {
  if (status === 'pending') return 'badge badge-warn';
  if (status === 'approved') return 'badge badge-ok';
  return 'badge badge-err';
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
}

function escHtml(str: string): string {
  const el = document.createElement('div');
  el.textContent = str;
  return el.innerHTML;
}

function escAttr(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}
